// Guided Tour — coach-mark style walkthrough for first-time clients.
//
// Why custom (no library):
//   The Standard's visual language (gold/black, Barlow Condensed, dense
//   information layout) is opinionated. driver.js / react-joyride / shepherd
//   all force their own tooltip chrome that fights with the brand. Building
//   this in-house is ~150 lines and gives perfect design fit + no runtime
//   cost beyond a single overlay element when active.
//
// How it works:
//   1. Caller invokes `startTour('client-home')` via the useGuidedTour hook.
//   2. The component locates each step's target element by CSS selector or
//      element id, snapshots its bounding rect, and renders a fixed-position
//      "cutout" div the same size as the target. That div's box-shadow uses
//      a 9999px spread of rgba(0,0,0,0.78) — a single-element trick that
//      dims everything outside the target without touching the target's
//      own styles.
//   3. A floating card appears above or below the target (whichever side
//      has room) with title / body / step counter / Skip + Back + Next.
//   4. On scroll / resize the rect recomputes so the cutout follows.
//   5. Last step's button reads "Got it" and writes the tour id to
//      localStorage so it never auto-fires again. The Skip button writes
//      the same flag so users who dismiss don't get re-pestered.
//   6. Same tour is replayable from the More drawer (the hook ignores the
//      localStorage flag when triggered manually via startTour({ replay }).
//
// Adding a new tour:
//   Append to TOUR_REGISTRY below. Each step needs a CSS selector or
//   element id that exists when the tour starts — if the element is on a
//   different page, navigate the user there first via the step's `before`
//   hook (e.g. `before: (nav) => nav('/client/progress')`).

import { useEffect, useLayoutEffect, useState, useRef, createContext, useContext, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'wouter';
import { X, ChevronRight, ChevronLeft, Sparkles } from 'lucide-react';

export type TourStep = {
  /** Element to highlight. Either a CSS selector or an id (with or without "#"). */
  target: string;
  /** Headline shown in the card. */
  title: string;
  /** Supporting paragraph. */
  body: string;
  /** Where the card sits relative to the target. 'auto' picks whichever side has more room. */
  placement?: 'top' | 'bottom' | 'auto';
  /**
   * Optional async hook fired before the step renders — use to navigate
   * to a different page so the target element exists in the DOM.
   * Receives the wouter `navigate(path)` function.
   */
  before?: (navigate: (path: string) => void) => Promise<void> | void;
  /** Extra dwell time after `before` resolves, in ms. Default 350ms. */
  beforeWaitMs?: number;
};

export type TourDefinition = {
  id: string;
  name: string;
  steps: TourStep[];
};

// ─── Tour registry ────────────────────────────────────────────────────────

export const TOUR_REGISTRY: Record<string, TourDefinition> = {
  'client-home': {
    id: 'client-home',
    name: 'Welcome to The Standard',
    steps: [
      {
        target: '[data-tour="today-hero"]',
        title: "This is your day",
        body: "Today's prompt + your scheduled session live here. Tap Start Session when you're ready to lift.",
        placement: 'bottom',
      },
      {
        target: '#daily-pillars',
        title: 'The three pillars',
        body: "Hit TRAIN, FUEL, and WORD every day. Tap each ring to mark it. All three = Standard met.",
        placement: 'top',
      },
      {
        target: '[data-tour="bottom-nav"]',
        title: 'Your five main destinations',
        body: "Home, Training, Nutrition, Community, and Me. Me opens your personal community profile. Use More in the header for the full app directory.",
        placement: 'top',
      },
      {
        target: '[data-tour="messages-icon"]',
        title: 'Coach is one tap away',
        body: 'Message me here anytime. PRs, struggles, questions, all of it. Faster than text.',
        placement: 'bottom',
      },
      {
        target: '[data-tour="more-button"]',
        title: 'Everything else',
        body: 'Devotional, Journal, Progress Hub, Body Command Center, Habits, Tasks. All under MORE. Tap it when you want to explore.',
        placement: 'top',
      },
    ],
  },

  // Phase 2a.2 — fires once when a returning client opens the redesigned Today.
  'today-focused-gut': {
    id: 'today-focused-gut',
    name: 'Today got sharper',
    steps: [
      {
        target: '[data-tour="score-hero"]',
        title: 'Your score, up top.',
        body: 'Same Standard Score, now the first thing you see.',
        placement: 'bottom',
        before: (navigate) => navigate('/client'),
      },
      {
        target: '[data-tour="pillars-rail"]',
        title: 'The Forge joined the rail.',
        body: 'Train, Fuel, Faith. And now community engagement counts too. The Forge is earned by showing up for your team.',
        placement: 'bottom',
      },
      {
        target: '[data-tour="one-thing"]',
        title: 'One thing today.',
        body: 'Coach Fe picks the single next action that matters most.',
        placement: 'bottom',
      },
      {
        target: '[data-tour="below-fold-toggle"]',
        title: 'Everything else is below.',
        body: 'Workout, tasks, devotional, stats. All here, just one scroll away.',
        placement: 'top',
      },
    ],
  },

  // Fires when a client first opens /client/progress. Walks them through
  // the five tabs + the custom-metrics feature so they actually use it
  // instead of bouncing off "wait what does Body do".
  'progress-hub': {
    id: 'progress-hub',
    name: 'Your Progress Hub',
    steps: [
      {
        target: '[data-tour="progress-tabs"]',
        title: 'One record, six tabs',
        body: 'Body, Training, Nutrition, Mindset, Standard score, Achievements. Everything you track lives behind these tabs.',
        placement: 'bottom',
      },
      {
        target: '[data-tour="progress-stats"]',
        title: "Today's snapshot",
        body: 'Latest weight, body fat, waist, and Standard score, with the change since an earlier entry.',
        placement: 'bottom',
      },
      {
        target: '[data-tour="progress-custom"]',
        title: 'Custom metrics',
        body: "Track anything: skeletal muscle mass, resting HR, sleep score. Tap Add field, name it, log values. Each card shows your latest reading and 30-day change.",
        placement: 'top',
      },
      {
        target: '[data-tour="progress-log"]',
        title: 'Log measurements',
        body: 'Tap Log body composition anytime to record weight, body fat, tape measurements, photos, and notes.',
        placement: 'bottom',
      },
    ],
  },

  // Fires the first time a client opens the community wall. Explains the
  // community mechanics so a new poster knows they can tag the room and
  // doesn't bounce off thinking "wait is this just a feed?"
  'wall': {
    id: 'wall',
    name: 'The Wall',
    steps: [
      {
        target: '[data-tour="wall-leaderboard"]',
        title: "The community scoreboard",
        body: 'Who showed up this week ranks here. Standard Score is the composite of all your training, nutrition, faith, and mindset.',
        placement: 'bottom',
      },
      {
        target: '[data-tour="wall-composer"]',
        title: 'Share with the room',
        body: "Drop a PR, a win, a struggle, a verse. Type @ to tag a specific member. They'll get a push. Photos and videos are welcome.",
        placement: 'bottom',
      },
      {
        target: '[data-tour="wall-categories"]',
        title: 'Filter by what matters',
        body: 'Tap a category to see only Wins, Faith, Training, Nutrition. Posts you make get tagged automatically.',
        placement: 'top',
      },
    ],
  },

  // Phase 2c.3 — fires once when the coach first opens the inbox post-deploy.
  'inbox-snooze-resurfacing': {
    id: 'inbox-snooze-resurfacing',
    name: 'Snooze got smarter',
    steps: [
      {
        target: '[data-tour="thread-list"]',
        title: 'Snoozed messages come back loud.',
        body: 'When a snooze ends, the conversation floats to the top with a gold "BACK" tag. No more quietly slipping back into the list.',
        placement: 'bottom',
      },
      {
        target: '[data-tour="thread-actions"]',
        title: 'Snooze from the thread menu.',
        body: 'Open the three-dot menu on any conversation and choose Snooze 24h. The thread leaves the active queue without being lost.',
        placement: 'bottom',
      },
      {
        target: '[data-tour="inbox-snoozed-filter"]',
        title: 'Your snoozed queue stays available.',
        body: 'Open Snoozed anytime to review parked conversations. When time is up, they return to the active list automatically.',
        placement: 'bottom',
      },
    ],
  },

  // Phase 2a.3 — fires once when the Today wins ribbon first has something to show
  // (viewer follows ≥1 person AND there's ≥1 recent win in the ribbon).
  'today-wins-ribbon': {
    id: 'today-wins-ribbon',
    name: 'Your people, today',
    steps: [
      {
        target: '[data-tour="wins-ribbon"]',
        title: 'Your people, today.',
        body: 'Wins from people you follow show up here. Tap to read. Tap Sharpened to back them.',
        placement: 'bottom',
      },
    ],
  },

  // Phase 2e.2 — fires once when a coach first opens the form-check review surface.
  'coach.formCheckReview.v1': {
    id: 'coach.formCheckReview.v1',
    name: 'Form check review',
    steps: [
      {
        target: '[data-tour="form-check-queue"]',
        title: 'New: form-check review',
        body: 'Pending submissions live here, oldest concerns first. Reviewed checks drop below the divider.',
        placement: 'bottom',
      },
      {
        target: '[data-tour="form-check-player"]',
        title: 'Scrub freely',
        body: 'Play and pause anywhere. Your reply composer can stamp the exact frame you are on.',
        placement: 'top',
      },
      {
        target: '[data-tour="form-check-insert-timestamp"]',
        title: 'Drop a timestamp',
        body: 'Inserts the current time into your reply. Timestamps become chips the athlete can tap to seek.',
        placement: 'top',
      },
    ],
  },
};

// ─── Hook / context ───────────────────────────────────────────────────────

type TourController = {
  /** Start a tour by id. Replays even if previously completed when force=true. */
  start: (tourId: string, opts?: { force?: boolean }) => void;
};

const TourCtx = createContext<TourController | null>(null);

export function useGuidedTour(): TourController {
  const ctx = useContext(TourCtx);
  return ctx ?? { start: () => { /* no-op when provider absent */ } };
}

function tourDoneKey(tourId: string) { return `tour.completed.${tourId}`; }

export function GuidedTourProvider({ children }: { children: React.ReactNode }) {
  const [activeTourId, setActiveTourId] = useState<string | null>(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [, navigate] = useLocation();

  const start = useCallback((tourId: string, opts?: { force?: boolean }) => {
    if (!opts?.force) {
      try {
        if (localStorage.getItem(tourDoneKey(tourId)) === 'true') return;
      } catch { /* private mode */ }
    }
    if (!TOUR_REGISTRY[tourId]) {
      console.warn(`[GuidedTour] unknown tour: ${tourId}`);
      return;
    }
    setActiveTourId(tourId);
    setStepIdx(0);
  }, []);

  const close = useCallback((markComplete: boolean) => {
    if (markComplete && activeTourId) {
      try { localStorage.setItem(tourDoneKey(activeTourId), 'true'); } catch { /* private mode */ }
    }
    setActiveTourId(null);
    setStepIdx(0);
  }, [activeTourId]);

  const tour = activeTourId ? TOUR_REGISTRY[activeTourId] : null;
  const step = tour?.steps[stepIdx] ?? null;

  // Run the step's `before` hook (e.g. navigate to a different page) before
  // we measure the target. We give the page ~350ms to settle so layout has
  // happened before we snapshot the bounding rect.
  const [hookSettled, setHookSettled] = useState(0); // counter — bumps each settle
  useEffect(() => {
    if (!step) return;
    let cancelled = false;
    const run = async () => {
      if (step.before) {
        await step.before(navigate);
      }
      await new Promise((r) => setTimeout(r, step.beforeWaitMs ?? 350));
      if (!cancelled) setHookSettled((n) => n + 1);
    };
    void run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTourId, stepIdx]);

  const next = () => {
    if (!tour) return;
    if (stepIdx >= tour.steps.length - 1) close(true);
    else setStepIdx((i) => i + 1);
  };
  const back = () => setStepIdx((i) => Math.max(0, i - 1));
  // Keep the context identity stable while the overlay advances. Consumers
  // auto-start tours in effects; recreating this object on every step change
  // makes those effects fire again and reset the tour back to step one.
  const controller = useMemo<TourController>(() => ({ start }), [start]);

  return (
    <TourCtx.Provider value={controller}>
      {children}
      {tour && step && (
        <TourOverlay
          key={`${tour.id}:${stepIdx}:${hookSettled}`}
          tour={tour}
          step={step}
          stepIdx={stepIdx}
          onNext={next}
          onBack={back}
          onSkip={() => close(true)}
        />
      )}
    </TourCtx.Provider>
  );
}

// ─── Overlay renderer ─────────────────────────────────────────────────────

function TourOverlay({
  tour, step, stepIdx, onNext, onBack, onSkip,
}: {
  tour: TourDefinition;
  step: TourStep;
  stepIdx: number;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const lastUpdate = useRef(0);
  const targetFound = useRef(false);
  const onTargetMissingRef = useRef(onNext);

  useEffect(() => { onTargetMissingRef.current = onNext; }, [onNext]);

  // Find the target. Selector strings starting with "#" are id lookups;
  // everything else goes through querySelector so we can target via the
  // data-tour="..." attribute pattern the rest of the app uses.
  const resolveTarget = useCallback((sel: string): HTMLElement | null => {
    if (!sel) return null;
    if (sel.startsWith('#')) return document.getElementById(sel.slice(1));
    if (/^[A-Za-z][\w-]*$/.test(sel)) {
      // bare id like "daily-pillars"
      const byId = document.getElementById(sel);
      if (byId) return byId;
    }
    try { return document.querySelector(sel) as HTMLElement | null; } catch { return null; }
  }, []);

  useLayoutEffect(() => {
    let cancelled = false;
    const measure = () => {
      const el = resolveTarget(step.target);
      if (!el) return;
      const r = el.getBoundingClientRect();
      const style = window.getComputedStyle(el);
      if (r.width < 1 || r.height < 1 || style.display === 'none' || style.visibility === 'hidden') return;
      if (!cancelled) {
        targetFound.current = true;
        setRect(r);
      }
    };

    targetFound.current = false;
    setRect(null);

    // First try — element may already be present.
    measure();

    // Retry briefly if not found (handles route transitions where the
    // element renders one tick after our measure).
    const retries = [60, 180, 400, 800, 1500];
    const timers = retries.map((ms) => setTimeout(measure, ms));
    const unavailableTimer = setTimeout(() => {
      if (!cancelled && !targetFound.current) onTargetMissingRef.current();
    }, 1750);

    // Keep the rect synced as the user scrolls / resizes / the page reflows.
    const onScrollResize = () => {
      const now = Date.now();
      if (now - lastUpdate.current < 16) return; // ~60fps
      lastUpdate.current = now;
      measure();
    };
    window.addEventListener('scroll', onScrollResize, true);
    window.addEventListener('resize', onScrollResize);
    window.visualViewport?.addEventListener('resize', onScrollResize);
    window.visualViewport?.addEventListener('scroll', onScrollResize);
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      clearTimeout(unavailableTimer);
      window.removeEventListener('scroll', onScrollResize, true);
      window.removeEventListener('resize', onScrollResize);
      window.visualViewport?.removeEventListener('resize', onScrollResize);
      window.visualViewport?.removeEventListener('scroll', onScrollResize);
    };
  }, [step.target, resolveTarget]);

  // When the target enters view, scroll it into the middle if it's off-screen.
  useEffect(() => {
    if (!rect) return;
    const viewportTop = window.visualViewport?.offsetTop ?? 0;
    const viewportBottom = viewportTop + (window.visualViewport?.height ?? window.innerHeight);
    const inView = rect.top >= viewportTop + 60 && rect.bottom <= viewportBottom - 100;
    if (inView) return;
    const el = resolveTarget(step.target);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [rect, step.target, resolveTarget]);

  // A target can be conditional (empty state, role, or viewport). Keep the
  // walkthrough invisible while it resolves; the timeout above advances to
  // the next useful step instead of exposing an implementation error.
  if (!rect) return null;

  // Compute card position
  const card = (() => {
    const viewportTop = window.visualViewport?.offsetTop ?? 0;
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    const viewportBottom = viewportTop + viewportHeight;
    const pad = 12;
    const cardMaxWidth = Math.min(360, window.innerWidth - 24);
    const cardEstHeight = 220;
    const spaceAbove = rect.top - viewportTop;
    const spaceBelow = viewportBottom - rect.bottom;
    const placement = step.placement === 'auto' || !step.placement
      ? (spaceBelow >= spaceAbove ? 'bottom' : 'top')
      : step.placement;
    let top = placement === 'bottom' ? rect.bottom + pad : rect.top - cardEstHeight - pad;
    if (top < viewportTop + pad) top = viewportTop + pad;
    if (top + cardEstHeight > viewportBottom - pad) top = viewportBottom - cardEstHeight - pad;
    let left = rect.left + rect.width / 2 - cardMaxWidth / 2;
    if (left < 12) left = 12;
    if (left + cardMaxWidth > window.innerWidth - 12) left = window.innerWidth - cardMaxWidth - 12;
    return { top, left, width: cardMaxWidth };
  })();

  const isLast = stepIdx === tour.steps.length - 1;

  return createPortal(
    <div className="fixed inset-0 z-[9999] pb-[env(safe-area-inset-bottom,0px)] pt-[env(safe-area-inset-top,0px)]" aria-live="polite">
      {/* Spotlight cutout — the box-shadow trick dims everything outside. */}
      <div
        style={{
          position: 'fixed',
          top: rect.top - 6,
          left: rect.left - 6,
          width: rect.width + 12,
          height: rect.height + 12,
          borderRadius: 14,
          boxShadow:
            '0 0 0 9999px rgba(0,0,0,0.78), 0 0 0 2px rgba(245,158,11,0.9), 0 0 32px rgba(245,158,11,0.4)',
          pointerEvents: 'none',
          transition: 'top 0.18s ease, left 0.18s ease, width 0.18s ease, height 0.18s ease',
        }}
      />

      {/* Click-shield over the rest of the page so users can't accidentally
          fire other UI mid-tour. The card itself stops propagation. */}
      <div
        className="absolute inset-0"
        onClick={onSkip}
        style={{ background: 'transparent', cursor: 'pointer' }}
      />

      {/* Floating card */}
      <div
        onClick={(e) => e.stopPropagation()}
        data-contrast="dark"
        className="premium-dark-surface fixed max-h-[calc(100dvh-1.5rem)] overflow-y-auto overscroll-contain rounded-2xl shadow-2xl"
        style={{
          top: card.top,
          left: card.left,
          width: card.width,
          background: '#141414',
          border: '1px solid rgba(245,158,11,0.35)',
          backdropFilter: 'blur(20px)',
        }}
      >
        <div className="flex items-center justify-between px-4 pt-3.5">
          <div className="flex items-center gap-1.5">
            <Sparkles className="w-3 h-3 text-primary" />
            <span className="text-[9px] font-black uppercase tracking-[0.25em] text-primary" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {tour.name}
            </span>
          </div>
          <button
            onClick={onSkip}
            className="flex size-11 items-center justify-center rounded-xl text-white/40 transition-colors hover:text-white/80"
            aria-label="Skip tour"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="px-4 pt-2 pb-3">
          <h3 className="text-base font-black text-white leading-tight mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {step.title}
          </h3>
          <p className="text-xs text-white/75 leading-relaxed">
            {step.body}
          </p>
        </div>
        <div className="flex items-center justify-between px-3 py-2 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
          <span className="text-[10px] font-bold uppercase tracking-widest text-white/40" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {stepIdx + 1} / {tour.steps.length}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={onSkip}
              className="text-[10px] font-bold uppercase tracking-widest text-white/40 hover:text-white/80 transition-colors px-2 py-1"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Skip
            </button>
            {stepIdx > 0 && (
              <button
                onClick={onBack}
                className="flex items-center gap-0.5 text-[10px] font-black uppercase tracking-widest text-white/70 hover:text-white px-2 py-1.5 rounded-md transition-colors"
                style={{ background: 'rgba(255,255,255,0.06)', fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                <ChevronLeft className="w-3 h-3" /> Back
              </button>
            )}
            <button
              onClick={onNext}
              className="flex items-center gap-0.5 text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-md transition-all active:scale-95"
              style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#000', fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              {isLast ? 'Got it' : 'Next'}
              {!isLast && <ChevronRight className="w-3 h-3" />}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
